import { useState, useEffect } from 'react'
import { formatVersion } from '../utils/version'
import { LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899']

/**
 * 将十进制版本号转换为十六进制格式
 * 例：65536 → 0x00010000
 */

export default function Stats() {
  const [dailyStats, setDailyStats] = useState<any[]>([])
  const [versionDist, setVersionDist] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      // 加载日活数据
      const dailyResponse = await fetch('/api/admin/apps/smart_clock/daily-stats?days=7')
      const dailyData = await dailyResponse.json()
      setDailyStats(dailyData.stats?.map((s: any) => ({
        date: s.stat_date.substring(5),  // MM-DD
        count: s.device_count
      })) || [])

      // 加载版本分布
      const versionResponse = await fetch('/api/admin/apps/smart_clock/devices/stats')
      const versionData = await versionResponse.json()
      
      const versionEntries = Object.entries(versionData.by_version || {})
      setVersionDist(versionEntries.map(([version, count]: [string, any]) => ({
        name: formatVersion(parseInt(version)),
        value: count
      })))
    } catch (error) {
      console.error('加载统计失败:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-400">加载中...</div>
  }

  return (
    <div className="space-y-6">
      {/* 日活趋势 */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">近 7 天活跃趋势</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={dailyStats}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="date" stroke="#94a3b8" />
              <YAxis stroke="#94a3b8" />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="count"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ fill: '#3b82f6' }}
                name="活跃设备数"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* 版本分布 */}
      <div className="grid grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">版本分布</h3>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={versionDist}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {versionDist.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '8px'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 统计摘要 */}
        <div className="card">
          <h3 className="text-lg font-semibold text-white mb-4">统计摘要</h3>
          <div className="space-y-4">
            <div className="bg-dark-border rounded-lg p-4">
              <p className="text-sm text-gray-400">总设备数</p>
              <p className="text-3xl font-bold text-white mt-1">
                {versionDist.reduce((sum, v) => sum + v.value, 0)}
              </p>
            </div>
            <div className="bg-dark-border rounded-lg p-4">
              <p className="text-sm text-gray-400">版本数量</p>
              <p className="text-3xl font-bold text-white mt-1">{versionDist.length}</p>
            </div>
            <div className="bg-dark-border rounded-lg p-4">
              <p className="text-sm text-gray-400">最新 7 天平均活跃</p>
              <p className="text-3xl font-bold text-white mt-1">
                {dailyStats.length > 0
                  ? Math.round(dailyStats.reduce((sum, d) => sum + d.count, 0) / dailyStats.length)
                  : 0}
              </p>
            </div>
            <div className="bg-dark-border rounded-lg p-4">
              <p className="text-sm text-gray-400">活跃峰值</p>
              <p className="text-3xl font-bold text-green-400 mt-1">
                {dailyStats.length > 0 ? Math.max(...dailyStats.map(d => d.count)) : 0}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
